import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';

export interface TeamConnectionStatus {
  /** The incident is already connected to the Team the tab is being added to. */
  connectedToThisTeam: boolean;
  /**
   * The incident is connected to some other Team site, so it cannot be
   * connected to this one.
   */
  connectedToAnotherTeam: boolean;
  /**
   * This Team site is already connected to a different incident or exercise
   * workspace.
   */
  teamLinkedElsewhere: boolean;
}

export const keys = {
  teamConnectionStatus: 'teamConnectionStatus',
};

/**
 * Looks ahead at what connecting this incident to this Team would do, so the
 * tab configuration page can warn the user while the Teams dialog is still
 * open. Read-only.
 */
export const useTeamConnectionStatus = (
  incidentId?: string,
  teamsId?: string
) =>
  useQuery<TeamConnectionStatus>({
    queryKey: [keys.teamConnectionStatus, incidentId, teamsId],
    queryFn: async () => {
      const response = await axios(config.apiGateway.teamConnectionStatus, {
        method: 'POST',
        data: { incident_id: incidentId, teams_id: teamsId },
      });

      return response.data;
    },
    enabled: Boolean(incidentId && teamsId),
    retry: false,
  });

export interface ConnectExistingTeamResult {
  connected: boolean;
  alreadyConnected: boolean;
  driveReady?: boolean;
}

export const useConnectExistingTeam = () =>
  useMutation<
    ConnectExistingTeamResult,
    unknown,
    { incidentId: string; teamsId: string }
  >({
    mutationFn: async ({ incidentId, teamsId }) => {
      const response = await axios(config.apiGateway.connectExistingTeam, {
        method: 'POST',
        data: { incident_id: incidentId, teams_id: teamsId },
      });

      return response.data;
    },
  });

export interface DisconnectExistingTeamResult {
  disconnected: boolean;
  alreadyDisconnected: boolean;
}

/**
 * Clears the incident's Teams linkage when the PRATUS tab is removed from the
 * Team. Nothing in Microsoft 365 is deleted and the incident can be reconnected
 * by adding the tab again.
 */
export const useDisconnectExistingTeam = () =>
  useMutation<
    DisconnectExistingTeamResult,
    unknown,
    { incidentId: string; teamsId: string }
  >({
    mutationFn: async ({ incidentId, teamsId }) => {
      const response = await axios(config.apiGateway.disconnectExistingTeam, {
        method: 'POST',
        data: { incident_id: incidentId, teams_id: teamsId },
      });

      return response.data;
    },
  });
